/**
 * ч.821: платформа → «AI-кредити»: баланси tenant'ів, поповнення/коригування,
 * використання за 30 днів. 1 кредит = credit_usd (USD), списання =
 * собівартість × markup.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { platformAiCredits } from '@/api/ai';
import TchTable from '@/shared/components/TchTable';

const fmt = (v: string | number) => Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AiCreditsTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['platform-ai-credits'], queryFn: platformAiCredits.list });
  const [tenantId, setTenantId] = useState('');
  const [credits, setCredits] = useState('100');
  const [note, setNote] = useState('');
  const [openUsage, setOpenUsage] = useState<string | null>(null);
  const usageQ = useQuery({
    queryKey: ['platform-ai-usage', openUsage],
    queryFn: () => platformAiCredits.usage(openUsage!),
    enabled: !!openUsage,
  });
  const topup = useMutation({
    mutationFn: () => platformAiCredits.topup({ tenant_id: tenantId, credits: Number(credits), note: note || null }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['platform-ai-credits'] }); setNote(''); },
  });
  const d = q.data;
  return (
    <div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
        1 кредит = ${d?.credit_usd ?? '…'}; списання = ціна постачальника (без ПДВ) × (1 + ПДВ {d ? Math.round(d.cost_vat * 100) : '…'}%) × {d?.markup ?? '…'}; модель {d?.model ?? '…'}.
        Перед викликом утримується оцінка вартості (HOLD), після — списується факт (CHARGE) і повертається залишок (RELEASE); недостатньо доступних → 402.
      </p>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <div className="field" style={{ minWidth: 260 }}>
          <label>Tenant</label>
          <select value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
            <option value="">—</option>
            {(d?.items ?? []).map((t) => <option key={t.tenant_id} value={t.tenant_id}>{t.name} ({t.plan})</option>)}
          </select>
        </div>
        <div className="field" style={{ width: 120 }}>
          <label>Кредитів</label>
          <input type="number" step="1" min="1" value={credits} onChange={(e) => setCredits(e.target.value)} />
        </div>
        <div className="field" style={{ minWidth: 220 }}>
          <label>Примітка</label>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="рахунок №… / бонус" />
        </div>
        <button className="primary" disabled={!tenantId || Number(credits) <= 0 || topup.isPending}
          onClick={() => topup.mutate()}>поповнити</button>
      </div>
      <TchTable>
        <thead>
          <tr>
            <th>Tenant</th><th style={{ width: 90 }}>План</th><th style={{ width: 110, textAlign: 'right' }}>Баланс</th>
            <th style={{ width: 90, textAlign: 'right' }} title="Утримано під поточні запити">Утримано</th>
            <th style={{ width: 90, textAlign: 'right' }}>Викликів 30д</th>
            <th style={{ width: 120, textAlign: 'right' }}>Собівартість 30д, $</th>
            <th style={{ width: 120, textAlign: 'right' }}>Списано 30д</th><th style={{ width: 100 }}></th>
          </tr>
        </thead>
        <tbody>
          {(d?.items ?? []).map((t) => (
            <tr key={t.tenant_id}>
              <td>
                {t.name}
                {t.is_tester && (
                  <span title="Тестовий акаунт: Oblik+/AI без обмежень, за рахунок платформи — кредити не списуються, собівартість у колонці «Собівартість 30д»"
                    style={{ marginLeft: 6, fontSize: '0.7rem', fontWeight: 700, padding: '0 4px', borderRadius: 4,
                      color: 'var(--badge-amber-fg)', background: 'var(--badge-amber-bg)', border: '1px solid var(--badge-amber-bd)' }}>
                    тестовий · без обмежень
                  </span>
                )}
                {t.is_platform_owned && !t.is_tester && (
                  <span title="Акаунт власника платформи: Oblik+/AI без обмежень, кредити не списуються, собівартість у колонці «Собівартість 30д»"
                    style={{ marginLeft: 6, fontSize: '0.7rem', fontWeight: 700, padding: '0 4px', borderRadius: 4,
                      color: 'var(--badge-blue-fg)', background: 'var(--badge-blue-bg)', border: '1px solid var(--badge-blue-bd)' }}>
                    власник платформи · без обмежень
                  </span>
                )}
              </td><td>{t.plan}</td>
              <td style={{ textAlign: 'right', color: Number(t.balance) <= 0 ? '#b91c1c' : undefined }}>{fmt(t.balance)}</td>
              <td style={{ textAlign: 'right' }}>{fmt(t.held)}</td>
              <td style={{ textAlign: 'right' }}>{t.calls_30d}</td>
              <td style={{ textAlign: 'right' }}>{Number(t.cost_usd_30d).toFixed(4)}</td>
              <td style={{ textAlign: 'right' }}>{fmt(t.charged_30d)}</td>
              <td><button type="button" onClick={() => setOpenUsage(openUsage === t.tenant_id ? null : t.tenant_id)}>використання</button></td>
            </tr>
          ))}
        </tbody>
      </TchTable>
      {openUsage && usageQ.data && (
        <div style={{ marginTop: '0.75rem' }}>
          <b style={{ fontSize: '0.85rem' }}>Останні виклики</b>
          <TchTable>
            <thead><tr><th>Коли</th><th>Функція</th><th>Організація</th><th>Модель</th>
              <th style={{ textAlign: 'right' }}>Токени вх/вих</th><th style={{ textAlign: 'right' }}>$</th>
              <th style={{ textAlign: 'right' }}>Кредити</th><th>Статус</th></tr></thead>
            <tbody>
              {usageQ.data.usage.map((u, i) => (
                <tr key={i}>
                  <td>{new Date(u.created_at).toLocaleString('uk-UA')}</td><td>{u.feature}</td><td>{u.organization ?? '—'}</td>
                  <td>{u.model}</td><td style={{ textAlign: 'right' }}>{u.input_tokens}/{u.output_tokens}</td>
                  <td style={{ textAlign: 'right' }}>{Number(u.cost_usd).toFixed(4)}</td>
                  <td style={{ textAlign: 'right' }}>{fmt(u.charged_credits)}</td><td>{u.status}</td>
                </tr>
              ))}
            </tbody>
          </TchTable>
          <b style={{ fontSize: '0.85rem' }}>Леджер кредитів</b>
          <TchTable>
            <thead><tr><th>Коли</th><th>Вид</th><th style={{ textAlign: 'right' }}>Кредити</th><th>Примітка</th></tr></thead>
            <tbody>
              {usageQ.data.ledger.map((l, i) => (
                <tr key={i}><td>{new Date(l.created_at).toLocaleString('uk-UA')}</td><td>{l.kind}</td>
                  <td style={{ textAlign: 'right' }}>{fmt(l.credits)}</td><td>{l.note ?? ''}</td></tr>
              ))}
            </tbody>
          </TchTable>
        </div>
      )}
    </div>
  );
}
